
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import BaseIndexPageConfig from './base-index-page.config';
import { CustomButtonComponent } from "../../widgets/custom-button/custom-button.component";
import { DataTableComponent } from "../../widgets/data-table/data-table.component";
import { HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { AlertNotificationService } from '../../../services/alert-notification.service';


@Component({
  selector: 'base-index-page',
  imports: [FormsModule, CustomButtonComponent, DataTableComponent],
  templateUrl: './base-index-page.component.html',
  styleUrl: './base-index-page.component.scss'
})
export class BaseIndexPageComponent implements OnInit {

  @Input() config?: BaseIndexPageConfig;
  @Input() service?: any;
  @Input() idKey: string = 'id';
  @Input() subSectionSettings?: string;
  @Input() extraParams?: Record<string, any> | null;
  @Input() autoLoad: boolean = true;

  @Output() onFiltersChanged = new EventEmitter<HttpParams>();

  pageSize: number = 25;
  currentPage: number = 0;
  sortField: string | null = null;
  sortOrder: 'asc' | 'desc' = 'asc';
  isExporting: boolean = false;

  private activeParams: HttpParams = new HttpParams();

  constructor(
    private router: Router,
    private alertService: AlertNotificationService
  ) { }

  ngOnInit(): void {

    if (this.config?.dataTable?.pagination?.pageSize) {
      this.pageSize = this.config?.dataTable.pagination?.pageSize
    }

    if (this.autoLoad) {
      this.loadData(0);
    }

  }

  /**
   * Reloads the table from the first page. Pass extraParams to apply them on this
   * very call, without waiting for the input binding to propagate.
   */
  reload(extraParams?: Record<string, any> | null) {
    if (extraParams !== undefined) {
      this.extraParams = extraParams;
    }
    this.loadData(0);
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize = pageSize;
    this.loadData(0)
  }

  onPageChange(page: number) {
    this.loadData(page);
  }

  onFilterChange(searchModel: any) {
    this.loadData(0);
    this.onFiltersChanged.emit(
      this.activeParams.delete('size').delete('page').delete('sort')
    );
  }

  onSortChange(sort: { sortField: string | null; sortOrder: 'asc' | 'desc' }) {
    this.sortField = sort.sortField;
    this.sortOrder = sort.sortOrder;
    this.loadData(0);
  }

  loadData(page: number) {

    this.currentPage = page;

    const searchModel = this.config?.dataTable.searchModel;

    let params = new HttpParams()
      .set('size', this.pageSize)
      .set('page', page);

    if (this.sortField && this.sortOrder) {
      params = params.set('sort', `${this.sortField},${this.sortOrder}`);
    }

    if (searchModel) {
      Object.keys(searchModel).forEach(i => searchModel[i] && (params = params.set(i, searchModel[i])));
    }

    if (this.extraParams) {
      Object.keys(this.extraParams).forEach(key => {
        const value = this.extraParams![key];
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, value);
        }
      });
    }

    this.activeParams = params;

    this.config?.apiListService({
      params: params
    }).subscribe({
      next: (response: any) => {
        const keys = this.config!.responseListModelName.split('.');
        let data = response.data;

        keys.forEach(key => {
          if (data) {
            data = data[key];
          }
        });

        this.config!.dataTable.data = data;
        this.config!.dataTable.pagination = response.pagination;
      },
      error: (error: any) => {
        if (error?.status === 403) {
          this.alertService.error('Access denied: you do not have permission to access this section.');
          this.router.navigate(['/app']);
          return;
        }
        this.alertService.error('An error occurred while loading the data.');
      }
    });

  }

  exportExcel(): void {
    if (!this.config?.exportExcelService || this.isExporting) {
      return;
    }

    this.isExporting = true;

    let exportParams = this.activeParams.delete('size').delete('page');

    this.config.exportExcelService(exportParams).subscribe({
      next: (response: any) => {
        const { fileName, mimeType, base64 } = response.data;
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
        this.isExporting = false;
      },
      error: () => {
        this.isExporting = false;
      }
    });
  }

}
